import React from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  ColumnLayout,
  Box,
  Tabs,
  StatusIndicator,
  Table,
  Link,
  Badge,
  ExpandableSection
} from '@cloudscape-design/components';

const DomainDetailsPanel = ({ domain, visible, onDismiss }) => {
  if (!domain) return null;

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Link>Edit</Link>
                <Link>Export</Link>
                <Link onClick={onDismiss}>Close</Link>
              </SpaceBetween>
            }
          >
            {domain.name}
          </Header>
        }
      >
        <SpaceBetween size="l">
          {/* Overview Section */}
          <ColumnLayout columns={2}>
            <Box>
              <SpaceBetween size="xs">
                <Box variant="awsui-key-label">Bounded Context</Box>
                <Box>{domain.context.bounded_context}</Box>
                <Box variant="awsui-key-label">Business Capability</Box>
                <Box>{domain.context.business_capability}</Box>
              </SpaceBetween>
            </Box>
            <Box>
              <SpaceBetween size="xs">
                <Box variant="awsui-key-label">Domain Type</Box>
                <StatusIndicator type={
                  domain.context.domain_type === 'CORE' ? 'success' :
                  domain.context.domain_type === 'SUPPORTING' ? 'info' :
                  'warning'
                }>
                  {domain.context.domain_type}
                </StatusIndicator>
                <Box variant="awsui-key-label">Domain Scope</Box>
                <Box>
                  <ul>
                    {domain.context.domain_scope.map((scope, index) => (
                      <li key={index}>{scope}</li>
                    ))}
                  </ul>
                </Box>
              </SpaceBetween>
            </Box>
          </ColumnLayout>

          {/* Detailed Tabs */}
          <Tabs
            tabs={[
              {
                label: "Domain Model",
                id: "model",
                content: (
                  <SpaceBetween size="l">
                    <Header variant="h3">Entities</Header>
                    {domain.domain_model.entities.map((entity, index) => (
                      <ExpandableSection
                        key={index}
                        header={
                          <Box>
                            {entity.name}{' '}
                            <Badge>{entity.role}</Badge>
                          </Box>
                        }
                      >
                        <SpaceBetween size="m">
                          <Table
                            columnDefinitions={[
                              { 
                                id: 'name',
                                header: "Attribute",
                                cell: item => item.name
                              },
                              { 
                                id: 'type',
                                header: "Type",
                                cell: item => item.type
                              },
                              { 
                                id: 'constraints',
                                header: "Constraints",
                                cell: item => item.constraints.join(', ')
                              },
                              { 
                                id: 'description',
                                header: "Description",
                                cell: item => item.description
                              }
                            ]}
                            items={entity.attributes}
                            variant="embedded"
                          />
                          {entity.business_rules.length > 0 && (
                            <ExpandableSection header="Business Rules">
                              <ul>
                                {entity.business_rules.map((rule, i) => (
                                  <li key={i}>{rule}</li>
                                ))}
                              </ul>
                            </ExpandableSection>
                          )}
                        </SpaceBetween>
                      </ExpandableSection>
                    ))}
                  </SpaceBetween>
                )
              },
              {
                label: "Operations",
                id: "operations",
                content: (
                  <Table
                    columnDefinitions={[
                      { 
                        id: 'name',
                        header: "Operation",
                        cell: item => item.name
                      },
                      { 
                        id: 'type',
                        header: "Type",
                        cell: item => (
                          <Badge color={item.type === 'COMMAND' ? 'blue' : 'green'}>
                            {item.type}
                          </Badge>
                        )
                      },
                      { 
                        id: 'entities',
                        header: "Entities Involved",
                        cell: item => item.entities_involved.join(', ')
                      },
                      { 
                        id: 'consistency',
                        header: "Consistency",
                        cell: item => (
                          <StatusIndicator type={
                            item.consistency_requirement === 'STRONG' ? 'success' : 'info'
                          }>
                            {item.consistency_requirement}
                          </StatusIndicator>
                        )
                      }
                    ]}
                    items={domain.business_operations}
                    variant="embedded"
                  />
                )
              },
              {
                label: "Service Contracts",
                id: "contracts",
                content: (
                  <SpaceBetween size="l">
                    <Table
                      columnDefinitions={[
                        { 
                          id: 'name',
                          header: "API",
                          cell: item => item.name
                        },
                        { 
                          id: 'method',
                          header: "Method",
                          cell: item => (
                            <Badge color={
                              item.type === 'GET' ? 'green' :
                              item.type === 'POST' ? 'blue' :
                              item.type === 'PUT' ? 'orange' : 'red'
                            }>
                              {item.type}
                            </Badge>
                          )
                        },
                        { 
                          id: 'path',
                          header: "Path",
                          cell: item => item.path
                        },
                        { 
                          id: 'description',
                          header: "Description",
                          cell: item => item.description
                        }
                      ]}
                      items={domain.service_contracts.apis}
                      variant="embedded"
                    />
                  </SpaceBetween>
                )
              },
              {
                label: "Implementation",
                id: "implementation",
                content: (
                  <SpaceBetween size="l">
                    <ColumnLayout columns={2}>
                      <Box>
                        <SpaceBetween size="xs">
                          <Box variant="awsui-key-label">Architectural Style</Box>
                          <Box>{domain.implementation_guidance.architectural_style}</Box>
                          <Box variant="awsui-key-label">Persistence Type</Box>
                          <Box>{domain.implementation_guidance.persistence.type}</Box>
                        </SpaceBetween>
                      </Box>
                      <Box>
                        <SpaceBetween size="xs">
                          <Box variant="awsui-key-label">Scaling Strategy</Box>
                          <Box>{domain.implementation_guidance.scaling.strategy}</Box>
                          <Box variant="awsui-key-label">Availability Requirement</Box>
                          <Box>{domain.implementation_guidance.reliability.availability_requirement}</Box>
                        </SpaceBetween>
                      </Box>
                    </ColumnLayout>
                    <ExpandableSection header="Data Characteristics">
                      <ColumnLayout columns={2}>
                        <Box>
                          <SpaceBetween size="xs">
                            <Box variant="awsui-key-label">Read Frequency</Box>
                            <Box>{domain.data_characteristics.volume_characteristics.read_frequency}</Box>
                            <Box variant="awsui-key-label">Write Frequency</Box>
                            <Box>{domain.data_characteristics.volume_characteristics.write_frequency}</Box>
                          </SpaceBetween>
                        </Box>
                        <Box>
                          <SpaceBetween size="xs">
                            <Box variant="awsui-key-label">Consistency Model</Box>
                            <Box>{domain.data_characteristics.consistency.type}</Box>
                            <Box variant="awsui-key-label">Data Growth</Box>
                            <Box>{domain.data_characteristics.volume_characteristics.data_growth}</Box>
                          </SpaceBetween>
                        </Box>
                      </ColumnLayout>
                    </ExpandableSection>
                  </SpaceBetween>
                )
              }
            ]}
          />
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default DomainDetailsPanel;
